import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { Observable, Subscription, of } from 'rxjs'
import { catchError, map, take, timeout, withLatestFrom } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'

import { Router } from '@angular/router'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { Dictionary } from 'highcharts'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Booking } from 'src/app/features/booking/booking'
import { bookingActions } from 'src/app/features/booking/booking.actions'
import { Location } from 'src/app/features/location/location'
import { locationSelectors } from 'src/app/features/location/location.selectors'
import { Member } from 'src/app/features/member/member'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { persistentUserRoleSelectors } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.selector'
import { SerieProposalStatus } from '../../serie-proposal'
import { serieProposalActions } from '../../serie-proposal.actions'
import { serieProposalSelectors } from '../../serie-proposal.selectors'
import { ConfirmationModalComponent } from '../../series-tab/confirmation-modal/confirmation-modal.component'
import {
  SerieProposalItem,
  SerieProposalItemStatus
} from '../serie-proposal-item'
import { serieProposalItemActions } from '../serie-proposal-item.actions'
import { serieProposalItemSelectors } from '../serie-proposal-item.selectors'
import { AccessRights } from 'src/app/features/role/user-role/user-role'

@Component({
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SerieProposalItemEditModal implements OnInit {
  @Input() serieProposalItem: SerieProposalItem
  @Input() member: Member
  @Input() additionalInfo: string = null
  @Output() onCancel = new EventEmitter<boolean>()
  public form: UntypedFormGroup
  public productEntities$: Observable<Dictionary<Product>>
  public selectedLanguage$: Observable<string>
  public products$: Observable<Product[]>
  public isLoading$: Observable<boolean>
  public serieProposalItemStatus = SerieProposalItemStatus
  public intersectingBookings: Booking[]
  public bsModalRefConfirmationModal: BsModalRef
  public locationEntities$: Observable<Dictionary<Location>>
  public accessRights = AccessRights
  private subscriptions: Subscription[] = []

  constructor(
    private bsModalRef: BsModalRef,
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private actions$: Actions,
    private notificationService: NotificationService,
    private router: Router,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      startDate: [null, [Validators.required]],
      startTime: [null, [Validators.required]],
      endTime: [null, [Validators.required]],
      dontSendMail: [false, [Validators.required]]
    })

    this.isLoading$ = this.store.select(
      serieProposalItemSelectors.selectIsLoading
    )

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.locationEntities$ = this.store.select(locationSelectors.selectEntities)

    this.form.controls.startTime.setValue(this.serieProposalItem.startDate)
    this.form.controls.endTime.setValue(
      moment(this.serieProposalItem.endDate).clone().add(1, 'minute')
    )

    this.fetchIntersectingBookings()

    this.actions$
      .pipe(
        ofType(bookingActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.intersectingBookings = action.entities
        }
      })
  }

  public showSendCounterProposal(): boolean {
    return Boolean(
      moment(this.serieProposalItem?.startDate)?.clone().format('YYYY-MM-DD') !=
        moment(this.form.value.startDate)?.clone().format('YYYY-MM-DD') ||
      moment(this.serieProposalItem?.startDate)?.clone().format('HH:mm') !=
        moment(this.form.value.startTime)?.clone().format('HH:mm') ||
      moment(this.serieProposalItem?.endDate)?.clone().format('HH:mm') !=
        moment(this.form.value.endTime)
          ?.clone()
          .subtract(1, 'minutes')
          .format('HH:mm')
    )
  }

  public onClickSendCounterProposal() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    const startTime = moment(this.form.value.startTime)
    const endTime = moment(this.form.value.endTime)
      .clone()
      .subtract(1, 'minute')

    const start = moment(
      `${this.form.value.startDate.format('YYYY-MM-DD')} ${startTime.format(
        'HH:mm'
      )}`,
      'YYYY-MM-DD HH:mm'
    )
    const end = moment(
      `${this.form.value.startDate.format('YYYY-MM-DD')} ${endTime.format(
        'HH:mm'
      )}`,
      'YYYY-MM-DD HH:mm'
    )

    if (end.isSameOrBefore(start)) {
      this.notificationService.emit(
        'employeeModule.endTimeEarlierThanStartTime',
        'warning'
      )
      return
    }

    this.store.dispatch(
      serieProposalItemActions.updateOneById({
        id: this.serieProposalItem.id,
        params: {
          startDate: moment(this.form.value.startDate).set({
            hour: startTime.get('hours'),
            minute: startTime.get('minutes'),
            second: 0
          }),
          endDate: moment(this.form.value.startDate).set({
            hour: endTime.get('hours'),
            minute: endTime.get('minutes'),
            second: 0
          }),
          status: SerieProposalItemStatus.COUNTER_PROPOSAL,
          dontSendMail: this.form.value.dontSendMail
        }
      })
    )

    this.handleUpsertManyAction()
  }

  public onClickDeclineProposalItem() {
    this.store.dispatch(
      serieProposalItemActions.updateOneById({
        id: this.serieProposalItem.id,
        params: {
          status: SerieProposalItemStatus.DECLINED
        }
      })
    )

    this.handleUpsertManyAction()
  }

  public onClickBookingRow(booking: Booking) {
    this.router.navigateByUrl(`/bookings/${booking.id}`)
  }

  public onSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      return
    }

    this.fetchIntersectingBookings()

    this.actions$
      .pipe(
        ofType(bookingActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.intersectingBookings = action.entities

          if (this.intersectingBookings?.length) {
            return this.notificationService.emit(
              'serieProposalModule.hasRecentConflictingBooking',
              'warning'
            )
          }

          this.store.dispatch(
            serieProposalItemActions.updateOneById({
              id: this.serieProposalItem.id,
              params: {
                id: this.serieProposalItem.id,
                status: SerieProposalItemStatus.ACCEPTED
              }
            })
          )

          this.handleUpsertManyAction()
        }
      })
  }

  public showConfirmationModal() {
    const mainModal = document.getElementById('mainModal')

    if (mainModal) {
      mainModal.classList.add('fadeModal')
    }

    this.bsModalRefConfirmationModal = this.modalService.show(
      ConfirmationModalComponent,
      {
        initialState: {
          title: 'serieProposalModule.hasRecentConflictingBooking',
          buttons: [
            {
              text: 'general.close',
              class: 'btn btn-soft-primary',
              accept: false
            },
            {
              text: 'general.accept',
              class: 'btn btn-primary',
              accept: true
            }
          ]
        },
        class: 'modal-md modal-dialog-scrollable'
      }
    )

    this.bsModalRefConfirmationModal.onHide
      .pipe(take(1))
      .subscribe((modal) => mainModal.classList.remove('fadeModal'))

    this.subscriptions.push(
      this.bsModalRefConfirmationModal.content.onAccept
        .pipe(take(1))
        .subscribe((acceptItemsWithConflictingBookings) => {
          mainModal.classList.remove('fadeModal')

          if (acceptItemsWithConflictingBookings) {
            this.store.dispatch(
              serieProposalItemActions.updateOneById({
                id: this.serieProposalItem.id,
                params: {
                  id: this.serieProposalItem.id,
                  status: SerieProposalItemStatus.ACCEPTED
                }
              })
            )

            return this.handleUpsertManyAction()
          }
        })
    )
  }

  public onClickRow(booking: Booking) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/bookings/${booking.id}`])
    )
    window.open(url, '_blank')
  }

  public onCheckSerieProposal() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        `/serie-proposals/${this.serieProposalItem.serieProposalId}`
      ])
    )
    window.open(url, '_blank')
  }

  public userHasAccessToSerieProposal$(): Observable<boolean> {
    return this.store
      .select(persistentUserRoleSelectors.selectSelectedUserRole)
      .pipe(
        withLatestFrom(
          this.store.select(
            serieProposalSelectors.selectById,
            this.serieProposalItem.serieProposalId
          )
        ),
        take(1),
        map(([pUr, serieProposal]) => {
          const siteId = pUr && pUr.siteId ? pUr.siteId : 0
          return siteId === serieProposal?.siteId
        })
      )
  }

  public cancel() {
    this.bsModalRef.hide()
  }

  private handleUpsertManyAction() {
    this.actions$
      .pipe(
        ofType(serieProposalItemActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (this.serieProposalItem) {
          if (this.serieProposalItem.serieProposalId) {
            this.store.dispatch(
              serieProposalActions.fetchOneById({
                id: this.serieProposalItem.serieProposalId,
                params: RequestQueryBuilder.create({
                  join: [
                    { field: 'customer' },
                    { field: 'organization' },
                    { field: 'organization.tags' },
                    { field: 'serieProposalItems' },
                    { field: 'serieProposalItems.saleItem' }
                  ]
                })
              })
            )
          }

          this.store.dispatch(
            serieProposalItemActions.fetchOneById({
              id: this.serieProposalItem.id,
              params: RequestQueryBuilder.create({
                join: [
                  { field: 'serieProposal' },
                  { field: 'linkedProduct' },
                  { field: 'linkedProduct.translations' }
                ]
              })
            })
          )
        }
        if (action) {
          this.notificationService.emit('alerts.success', 'success')
          this.onCancel.emit(true)
          this.cancel()
        }
      })
  }

  private fetchIntersectingBookings() {
    if (!this.serieProposalItem) {
      return
    }

    this.productEntities$.pipe(take(1)).subscribe((productEntities) => {
      let dependantProductIds: number[] = Array.from(
        new Set(
          productEntities[this.serieProposalItem.productId]
            ?.dependantProducts ?? []
        )
      )

      const productIds = Array.from(
        new Set([...dependantProductIds, this.serieProposalItem.productId])
      )

      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              id: {
                [CondOperator.NOT_EQUALS]: this.serieProposalItem.bookingId
              },
              startDate: {
                [CondOperator.LOWER_THAN]: moment(
                  this.serieProposalItem.endDate
                ).format('YYYY-MM-DD HH:mm')
              },
              endDate: {
                [CondOperator.GREATER_THAN]: moment(
                  this.serieProposalItem.startDate
                ).format('YYYY-MM-DD HH:mm')
              },
              $or: [
                productIds?.length
                  ? {
                      productId: {
                        [CondOperator.IN]: productIds
                      }
                    }
                  : null,
                productIds?.length
                  ? {
                      linkedProductId: {
                        [CondOperator.IN]: productIds
                      }
                    }
                  : null
              ].filter((c) => Boolean(c))
            }
          })
        })
      )
    })
  }
}
